export class AiChatWidget {
    static instances = new Map();

    static getInstance(appId) {
        if (appId) {
            return AiChatWidget.instances.get(appId) || null;
        }

        return AiChatWidget.instances.values().next().value || null;
    }

    constructor(appId, options = {}) {
        this.appId = appId;
        this.apiBaseUrl = options.apiBaseUrl || this.detectApiBaseUrl();
        this.wsUrl = options.wsUrl || null;
        this.ws = null;
        this.isConnecting = false;
        this.currentSessionId = null;
        this.config = null;
        this.welcomeShown = false;
        this.connectionStatus = 'connecting';
        this.translations = {
            nl: {
                placeholder: 'Stel je vraag...',
                isTyping: 'is aan het typen...',
                connecting: 'Verbinden met chat...',
                connected: 'Verbonden',
                disconnected: 'Geen verbinding. Opnieuw proberen...',
                sendFailed: 'Bericht niet verzonden. Wacht op verbinding.',
                serverError: 'Er ging iets mis. Probeer opnieuw.',
            },
            en: {
                placeholder: 'Ask your question...',
                isTyping: 'is typing...',
                connecting: 'Connecting to chat...',
                connected: 'Connected',
                disconnected: 'No connection. Retrying...',
                sendFailed: 'Message not sent. Waiting for connection.',
                serverError: 'Something went wrong. Please try again.',
            },
        };
        this.currentLang = navigator.language.startsWith('nl') ? 'nl' : 'en';
        this.sessionTtlMs = 20 * 60 * 1000;
        this.messageHistory = [];
        this.pendingResumeSessionUuid = null;
        this.pendingOutboundMessage = null;
        AiChatWidget.instances.set(appId, this);
        this.init();
    }

    detectApiBaseUrl() {
        const scriptTag = document.currentScript || document.querySelector('script[data-app-id]');
        return scriptTag?.getAttribute('data-api-base-url')
            || `${window.location.origin}/api/v1`;
    }

    async init() {
        await this.fetchConfig();
        this.setupElements();
        this.setupEventListeners();
        this.setupViewportHandling();
        this.restorePersistedState();
        this.connectWebSocket();
        this.setupExternalTriggers();
    }

    getStorageKey() {
        return `ai_chat_widget_${this.appId}`;
    }

    loadPersistedState() {
        if (typeof Storage === 'undefined') {
            return null;
        }

        try {
            const raw = localStorage.getItem(this.getStorageKey());
            if (!raw) {
                return null;
            }

            const state = JSON.parse(raw);
            if (!state?.updatedAt || Date.now() - state.updatedAt > this.sessionTtlMs) {
                localStorage.removeItem(this.getStorageKey());
                return null;
            }

            return state;
        } catch {
            localStorage.removeItem(this.getStorageKey());
            return null;
        }
    }

    persistState() {
        if (typeof Storage === 'undefined' || !this.currentSessionId) {
            return;
        }

        localStorage.setItem(this.getStorageKey(), JSON.stringify({
            sessionUuid: this.currentSessionId,
            messages: this.messageHistory,
            welcomeShown: this.welcomeShown,
            updatedAt: Date.now(),
        }));
    }

    clearPersistedState() {
        if (typeof Storage !== 'undefined') {
            localStorage.removeItem(this.getStorageKey());
        }
    }

    restorePersistedState() {
        const state = this.loadPersistedState();
        if (!state) {
            return;
        }

        this.pendingResumeSessionUuid = state.sessionUuid || null;
        this.welcomeShown = Boolean(state.welcomeShown);
        this.messageHistory = Array.isArray(state.messages) ? state.messages : [];

        this.messageHistory.forEach((message) => {
            this.renderMessage(message, { persist: false });
        });
    }

    clearMessagesUi() {
        if (this.elements.messages) {
            this.elements.messages.innerHTML = '';
        }
        this.messageHistory = [];
    }

    async fetchConfig() {
        const response = await fetch(`${this.apiBaseUrl}/apps/${this.appId}/ai-chat/config`);
        if (!response.ok) {
            throw new Error('AI chat config not found');
        }
        this.config = await response.json();
        this.wsUrl = this.wsUrl || this.config.ws_url;
    }

    setupElements() {
        const style = document.createElement('style');
        style.textContent = `
            .ai-chat-widget-launcher,.ai-chat-widget-container,.ai-chat-widget-container *{box-sizing:border-box!important;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important}
            .ai-chat-widget-launcher{position:fixed!important;bottom:20px!important;right:20px!important;z-index:999999!important;display:flex!important;flex-direction:column!important;align-items:flex-end!important;gap:10px!important}
            .ai-chat-widget-launcher.is-hidden{display:none!important}
            .ai-chat-widget-bubble{background:#fff!important;color:#111!important;padding:10px 14px!important;border-radius:14px!important;box-shadow:0 4px 16px rgba(0,0,0,.14)!important;font-size:14px!important;line-height:1.35!important;max-width:220px!important;position:relative!important;cursor:pointer!important}
            .ai-chat-widget-bubble::after{content:''!important;position:absolute!important;bottom:-6px!important;right:24px!important;width:12px!important;height:12px!important;background:#fff!important;transform:rotate(45deg)!important;box-shadow:2px 2px 4px rgba(0,0,0,.08)!important}
            .ai-chat-widget-button{width:60px!important;height:60px!important;border-radius:50%!important;background-color:${this.config?.base_color||'#007bff'}!important;border:none!important;cursor:pointer!important;display:flex!important;align-items:center!important;justify-content:center!important;color:#fff!important;font-size:24px!important;box-shadow:0 4px 16px rgba(0,0,0,.18)!important;overflow:hidden!important;padding:0!important}
            .ai-chat-widget-button.has-icon{background:transparent!important;box-shadow:0 4px 16px rgba(0,0,0,.18)!important}
            .ai-chat-widget-container{position:fixed!important;bottom:90px!important;right:20px!important;width:380px!important;max-width:calc(100vw - 40px)!important;height:560px!important;background:#fff!important;border-radius:16px!important;box-shadow:0 5px 20px rgba(0,0,0,.2)!important;display:none;flex-direction:column!important;z-index:1000000!important;overflow:hidden!important}
            .ai-chat-header{background:${this.config?.base_color||'#007bff'}!important;color:#fff!important;padding:12px 16px!important;display:flex!important;justify-content:space-between!important;align-items:center!important;gap:12px!important;flex-shrink:0!important}
            .ai-chat-header-title{display:flex!important;align-items:center!important;gap:10px!important;min-width:0!important}
            .ai-chat-header-icon{width:36px!important;height:36px!important;border-radius:50%!important;object-fit:cover!important;flex-shrink:0!important;border:2px solid rgba(255,255,255,.35)!important}
            .ai-chat-header-title strong{font-size:15px!important;line-height:1.2!important}
            .ai-chat-messages{flex:1!important;min-height:0!important;overflow-y:auto!important;padding:16px!important;display:flex!important;flex-direction:column!important;gap:10px!important;background:#f0f2f5!important;-webkit-overflow-scrolling:touch!important}
            .ai-message{max-width:88%!important;font-size:14px!important;line-height:1.45!important}
            .ai-message.question{background:${this.config?.base_color||'#007bff'}!important;color:#fff!important;margin-left:auto!important;padding:10px 14px!important;border-radius:18px!important}
            .ai-message.answer{background:#fff!important;color:#111!important;margin-right:auto!important;padding:10px 14px!important;border-radius:18px!important;box-shadow:0 1px 2px rgba(0,0,0,.1)!important}
            .ai-message.answer.has-cards{max-width:100%!important;width:100%!important;background:transparent!important;box-shadow:none!important;padding:0!important}
            .ai-message-intro{background:#fff!important;color:#111!important;padding:10px 14px!important;border-radius:18px!important;box-shadow:0 1px 2px rgba(0,0,0,.1)!important;margin-bottom:8px!important;font-size:14px!important;line-height:1.45!important}
            .ai-profile-cards{display:flex!important;flex-direction:column!important;gap:8px!important}
            .ai-profile-card{background:#fff!important;border:1px solid #e4e6eb!important;border-radius:14px!important;padding:12px 14px!important;box-shadow:0 1px 3px rgba(0,0,0,.06)!important;display:flex!important;gap:12px!important;align-items:flex-start!important}
            .ai-profile-card-logo{width:48px!important;height:48px!important;border-radius:10px!important;object-fit:cover!important;flex-shrink:0!important;background:#f0f2f5!important}
            .ai-profile-card-body{flex:1!important;min-width:0!important}
            .ai-profile-card-title{margin:0 0 4px!important;font-size:15px!important;font-weight:600!important;color:#111!important;line-height:1.3!important}
            .ai-profile-card-category{display:inline-block!important;font-size:11px!important;font-weight:600!important;color:${this.config?.base_color||'#007bff'}!important;background:rgba(0,123,255,.08)!important;padding:2px 8px!important;border-radius:999px!important;margin-bottom:6px!important}
            .ai-profile-card-meta{font-size:12px!important;color:#65676b!important;margin:2px 0!important}
            .ai-profile-card-description{font-size:12px!important;color:#444!important;margin-top:6px!important;line-height:1.4!important}
            .ai-profile-card-rating{font-size:12px!important;color:#f59e0b!important;margin-top:4px!important}
            .ai-profile-card-actions{display:flex!important;flex-wrap:wrap!important;gap:6px!important;margin-top:10px!important}
            .ai-profile-card-actions a{font-size:12px!important;padding:6px 10px!important;border-radius:8px!important;background:#f0f2f5!important;text-decoration:none!important;color:#111!important;font-weight:500!important}
            .ai-profile-card-actions a:hover{background:#e4e6eb!important}
            .ai-discover-more{margin-top:8px!important;padding:10px 14px!important;background:#fff!important;border-radius:14px!important;box-shadow:0 1px 2px rgba(0,0,0,.1)!important;font-size:13px!important}
            .ai-discover-more a{color:${this.config?.base_color||'#007bff'}!important;font-weight:600!important;text-decoration:none!important}
            .ai-chat-status{padding:8px 16px!important;font-size:12px!important;border-bottom:1px solid #eee!important;background:#fff!important;color:#65676b!important;flex-shrink:0!important}
            .ai-chat-status.is-error{color:#b42318!important;background:#fef3f2!important}
            .ai-chat-status.is-connected{color:#027a48!important;background:#ecfdf3!important}
            .ai-chat-input-container{padding:16px!important;border-top:1px solid #eee!important;display:flex!important;gap:8px!important;background:#fff!important;flex-shrink:0!important}
            .ai-chat-input{flex:1!important;border:1px solid #ddd!important;border-radius:20px!important;padding:8px 12px!important;font-size:16px!important}
            .ai-send-button{background:${this.config?.base_color||'#007bff'}!important;color:#fff!important;border:none!important;border-radius:50%!important;width:36px!important;height:36px!important;cursor:pointer!important}
            .ai-typing-indicator{display:flex!important;align-items:center!important;gap:8px!important;color:#65676b!important}
            .ai-typing-dots{display:inline-flex!important;align-items:center!important;gap:4px!important;height:16px!important}
            .ai-typing-dots span{width:6px!important;height:6px!important;border-radius:50%!important;background:${this.config?.base_color||'#007bff'}!important;opacity:.35!important;animation:ai-typing-bounce 1.2s infinite ease-in-out!important}
            .ai-typing-dots span:nth-child(2){animation-delay:.15s!important}
            .ai-typing-dots span:nth-child(3){animation-delay:.3s!important}
            @keyframes ai-typing-bounce{0%,60%,100%{transform:translateY(0);opacity:.35}30%{transform:translateY(-5px);opacity:1}}
            @media (max-width:768px){
                .ai-chat-widget-container.is-mobile-popup{left:20px!important;right:20px!important;bottom:20px!important;width:auto!important;max-width:none!important;height:min(560px,75dvh)!important;border-radius:16px!important;box-shadow:0 5px 20px rgba(0,0,0,.2)!important}
                .ai-chat-widget-container.is-mobile-popup.is-keyboard-open{bottom:auto!important}
            }
        `;
        document.head.appendChild(style);

        this.elements = {
            launcher: document.createElement('div'),
            bubble: null,
            button: document.createElement('button'),
            container: document.createElement('div'),
        };

        this.elements.launcher.className = 'ai-chat-widget-launcher';

        const bubbleText = this.getLauncherBubbleText();
        if (bubbleText) {
            this.elements.bubble = document.createElement('div');
            this.elements.bubble.className = 'ai-chat-widget-bubble';
            this.elements.bubble.textContent = bubbleText;
            this.elements.launcher.appendChild(this.elements.bubble);
        }

        this.elements.button.className = 'ai-chat-widget-button';
        if (this.config?.chat_icon_url) {
            this.elements.button.classList.add('has-icon');
            this.elements.button.innerHTML = `<img src="${this.config.chat_icon_url}" alt="AI Chat" style="width:100%;height:100%;border-radius:50%;object-fit:cover">`;
        } else {
            this.elements.button.innerHTML = '🤖';
        }

        this.elements.launcher.appendChild(this.elements.button);

        this.elements.container.className = 'ai-chat-widget-container';
        this.elements.container.innerHTML = `
            <div class="ai-chat-header">
                <div class="ai-chat-header-title">
                    ${this.getHeaderIconHtml()}
                    <strong>${this.config?.operator_name || 'AI Assistent'}</strong>
                </div>
                <button type="button" class="ai-close-chat" style="background:none;border:none;color:#fff;font-size:24px;cursor:pointer;flex-shrink:0">×</button>
            </div>
            <div class="ai-chat-status">${this.translations[this.currentLang].connecting}</div>
            <div class="ai-chat-messages"></div>
            <div class="ai-chat-input-container">
                <input type="text" class="ai-chat-input" placeholder="${this.translations[this.currentLang].placeholder}">
                <button type="button" class="ai-send-button">➤</button>
            </div>
        `;

        document.body.appendChild(this.elements.launcher);
        document.body.appendChild(this.elements.container);

        this.elements.messages = this.elements.container.querySelector('.ai-chat-messages');
        this.elements.status = this.elements.container.querySelector('.ai-chat-status');
        this.elements.input = this.elements.container.querySelector('.ai-chat-input');
        this.elements.sendButton = this.elements.container.querySelector('.ai-send-button');
        this.elements.closeButton = this.elements.container.querySelector('.ai-close-chat');
        this.setInputEnabled(false);
    }

    getHeaderIconHtml() {
        if (!this.config?.chat_icon_url) {
            return '';
        }

        return `<img src="${this.config.chat_icon_url}" alt="" class="ai-chat-header-icon">`;
    }

    getLauncherBubbleText() {
        const bubbleMessage = this.config?.launcher_bubble_message;
        if (!bubbleMessage) {
            return '';
        }

        const message = bubbleMessage[this.currentLang] || bubbleMessage.nl || bubbleMessage.en;
        return typeof message === 'string' ? message.trim() : '';
    }

    isMobileLayout() {
        return window.matchMedia('(max-width: 768px)').matches;
    }

    setupViewportHandling() {
        this.handleViewportChange = () => this.updateMobileLayout();

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.handleViewportChange);
            window.visualViewport.addEventListener('scroll', this.handleViewportChange);
        }

        window.addEventListener('resize', this.handleViewportChange);
    }

    getKeyboardInset() {
        const viewport = window.visualViewport;
        if (!viewport) {
            return 0;
        }

        return Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    }

    updateMobileLayout() {
        const container = this.elements.container;
        if (!container || container.style.display !== 'flex' || !this.isMobileLayout()) {
            return;
        }

        const viewport = window.visualViewport;
        const keyboardInset = this.getKeyboardInset();
        const margin = 12;

        container.classList.add('is-mobile-popup');

        if (keyboardInset > 0 && viewport) {
            container.classList.add('is-keyboard-open');
            container.style.top = `${viewport.offsetTop + margin}px`;
            container.style.left = `${viewport.offsetLeft + margin}px`;
            container.style.width = `${Math.max(0, viewport.width - margin * 2)}px`;
            container.style.height = `${Math.max(0, viewport.height - margin * 2)}px`;
            container.style.bottom = 'auto';
            container.style.right = 'auto';
        } else {
            container.classList.remove('is-keyboard-open');
            container.style.top = '';
            container.style.left = '';
            container.style.width = '';
            container.style.height = '';
            container.style.bottom = '';
            container.style.right = '';
        }
    }

    resetMobileLayout() {
        const container = this.elements.container;
        if (!container) {
            return;
        }

        container.classList.remove('is-mobile-popup', 'is-keyboard-open');
        container.style.top = '';
        container.style.left = '';
        container.style.width = '';
        container.style.height = '';
        container.style.bottom = '';
        container.style.right = '';
    }

    scrollMessagesToBottom() {
        if (!this.elements.messages) {
            return;
        }

        this.elements.messages.scrollTop = this.elements.messages.scrollHeight;
    }

    scheduleScrollToBottom() {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.scrollMessagesToBottom();
            });
        });
    }

    setupEventListeners() {
        this.elements.button.addEventListener('click', () => this.openChat());
        this.elements.bubble?.addEventListener('click', () => this.openChat());
        this.elements.closeButton.addEventListener('click', () => this.closeChat());
        this.elements.sendButton.addEventListener('click', () => this.sendMessage());
        this.elements.input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') this.sendMessage();
        });
        this.elements.input.addEventListener('focus', () => {
            requestAnimationFrame(() => {
                this.updateMobileLayout();
                this.scheduleScrollToBottom();
            });
        });
        this.elements.input.addEventListener('blur', () => {
            requestAnimationFrame(() => this.updateMobileLayout());
        });
    }

    setConnectionStatus(status, message, isError = false) {
        this.connectionStatus = status;
        if (!this.elements.status) {
            return;
        }

        this.elements.status.textContent = message;
        this.elements.status.classList.toggle('is-error', isError);
        this.elements.status.classList.toggle('is-connected', status === 'connected');
    }

    setInputEnabled(enabled) {
        if (!this.elements.input || !this.elements.sendButton) {
            return;
        }

        this.elements.input.disabled = !enabled;
        this.elements.sendButton.disabled = !enabled;
    }

    connectWebSocket() {
        if (this.isConnecting || !this.wsUrl) {
            if (!this.wsUrl) {
                this.setConnectionStatus('error', 'WebSocket URL ontbreekt', true);
            }
            return;
        }

        this.isConnecting = true;
        this.setConnectionStatus('connecting', this.translations[this.currentLang].connecting);
        this.setInputEnabled(false);

        const resumeQuery = this.pendingResumeSessionUuid
            ? `&sessionId=${encodeURIComponent(this.pendingResumeSessionUuid)}`
            : '';

        this.ws = new WebSocket(`${this.wsUrl}?appId=${encodeURIComponent(this.appId)}${resumeQuery}`);

        this.ws.onopen = () => {
            this.isConnecting = false;
            this.setConnectionStatus('connected', this.translations[this.currentLang].connected);
            this.setInputEnabled(true);
        };

        this.ws.onclose = () => {
            this.isConnecting = false;
            this.setConnectionStatus('disconnected', this.translations[this.currentLang].disconnected, true);
            this.setInputEnabled(false);
            setTimeout(() => this.connectWebSocket(), 3000);
        };

        this.ws.onerror = () => {
            this.isConnecting = false;
            this.setConnectionStatus('disconnected', this.translations[this.currentLang].disconnected, true);
            this.setInputEnabled(false);
        };

        this.ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.type === 'connection') {
                if (this.pendingResumeSessionUuid && !data.resumed) {
                    this.clearPersistedState();
                    this.clearMessagesUi();
                    this.welcomeShown = false;
                }

                this.currentSessionId = data.sessionId;
                this.pendingResumeSessionUuid = null;
                this.persistState();
                this.flushPendingOutboundMessage();
                this.setConnectionStatus('connected', this.translations[this.currentLang].connected);
                this.setInputEnabled(true);
            } else if (data.type === 'message') {
                this.removeTypingIndicator();
                this.addMessage(data.content, 'answer', data.profiles || null, data.discoverMore || null);
                this.setInputEnabled(true);
            } else if (data.type === 'error') {
                this.removeTypingIndicator();
                this.addMessage(data.message || this.translations[this.currentLang].serverError, 'answer');
                this.setInputEnabled(true);
            }
        };
    }

    open(options = {}) {
        this.openChat(options);
    }

    openWithMessage(message, options = {}) {
        const text = message?.trim();
        if (!text) {
            this.open(options);
            return;
        }

        if (!options.showWelcome) {
            this.welcomeShown = true;
        }

        this.open({ ...options, skipWelcome: !options.showWelcome });
        this.enqueueOrSendMessage(text);
    }

    openChat(options = {}) {
        this.elements.container.style.display = 'flex';
        this.elements.launcher.classList.add('is-hidden');
        this.updateMobileLayout();
        if (!this.welcomeShown && !options.skipWelcome && this.messageHistory.length === 0) {
            this.showWelcomeMessage();
            this.welcomeShown = true;
            this.persistState();
        }
        this.scheduleScrollToBottom();
    }

    canSendMessages() {
        return this.ws?.readyState === WebSocket.OPEN && Boolean(this.currentSessionId);
    }

    enqueueOrSendMessage(message) {
        const text = message?.trim();
        if (!text) {
            return false;
        }

        if (this.canSendMessages()) {
            this.dispatchMessage(text);
            return true;
        }

        this.pendingOutboundMessage = text;
        this.addMessage(text, 'question');
        this.showTypingIndicator();
        return false;
    }

    dispatchMessage(message) {
        this.addMessage(message, 'question');
        this.showTypingIndicator();
        this.ws.send(JSON.stringify({ type: 'message', content: message }));
    }

    flushPendingOutboundMessage() {
        if (!this.pendingOutboundMessage || !this.canSendMessages()) {
            return;
        }

        const message = this.pendingOutboundMessage;
        this.pendingOutboundMessage = null;
        this.ws.send(JSON.stringify({ type: 'message', content: message }));
    }

    resolveExternalMessage(source) {
        let message = source.getAttribute('data-ai-chat-message')?.trim() || '';

        const inputSelector = source.getAttribute('data-ai-chat-input');
        if (inputSelector) {
            const input = document.querySelector(inputSelector);
            if (input) {
                message = input.value.trim() || message;
            }
        }

        return message;
    }

    clearExternalInput(source) {
        if (source.hasAttribute('data-ai-chat-keep-input')) {
            return;
        }

        const inputSelector = source.getAttribute('data-ai-chat-input');
        if (!inputSelector) {
            return;
        }

        const input = document.querySelector(inputSelector);
        if (input) {
            input.value = '';
        }
    }

    matchesAppScope(element) {
        const scopeAppId = element.getAttribute('data-app-id');
        return !scopeAppId || scopeAppId === this.appId;
    }

    setupExternalTriggers() {
        document.querySelectorAll('form[data-ai-chat-form]').forEach((form) => {
            if (!this.matchesAppScope(form)) {
                return;
            }

            form.addEventListener('submit', (event) => {
                event.preventDefault();

                const input = form.querySelector('[data-ai-chat-input]')
                    || form.querySelector('[name="message"]')
                    || form.querySelector('input[type="text"], input[type="search"], textarea');

                const message = input?.value?.trim();
                if (!message) {
                    return;
                }

                this.openWithMessage(message);
                if (!form.hasAttribute('data-ai-chat-keep-input') && input) {
                    input.value = '';
                }
            });
        });

        document.querySelectorAll('[data-ai-chat-trigger]').forEach((trigger) => {
            if (!this.matchesAppScope(trigger)) {
                return;
            }

            trigger.addEventListener('click', (event) => {
                event.preventDefault();

                const message = this.resolveExternalMessage(trigger);
                if (message) {
                    this.openWithMessage(message);
                    this.clearExternalInput(trigger);
                    return;
                }

                this.open();
            });
        });
    }

    closeChat() {
        this.elements.container.style.display = 'none';
        this.elements.launcher.classList.remove('is-hidden');
        this.resetMobileLayout();
    }

    addMessage(text, type, profiles = null, discoverMore = null, options = {}) {
        this.renderMessage({ type, text, profiles, discoverMore }, options);
    }

    renderMessage(message, options = {}) {
        const { persist = true } = options;
        const { text, type, profiles = null, discoverMore = null } = message;

        const messageDiv = document.createElement('div');
        messageDiv.className = `ai-message ${type}${profiles?.length || discoverMore ? ' has-cards' : ''}`;

        if (profiles?.length) {
            const intro = document.createElement('div');
            intro.className = 'ai-message-intro';
            intro.textContent = text;
            messageDiv.appendChild(intro);

            const cardsContainer = document.createElement('div');
            cardsContainer.className = 'ai-profile-cards';

            profiles.forEach((profile) => {
                cardsContainer.appendChild(this.buildProfileCard(profile));
            });

            messageDiv.appendChild(cardsContainer);
        } else if (discoverMore) {
            const intro = document.createElement('div');
            intro.className = 'ai-message-intro';
            intro.textContent = text;
            messageDiv.appendChild(intro);
        } else {
            messageDiv.textContent = text;
        }

        if (discoverMore?.url) {
            messageDiv.appendChild(this.buildDiscoverMore(discoverMore));
        }

        this.elements.messages.appendChild(messageDiv);
        this.scrollMessagesToBottom();

        if (persist) {
            this.messageHistory.push({ type, text, profiles, discoverMore });
            this.persistState();
        }
    }

    buildDiscoverMore(discoverMore) {
        const wrapper = document.createElement('div');
        wrapper.className = 'ai-discover-more';

        const link = document.createElement('a');
        link.href = discoverMore.url;
        link.textContent = discoverMore.label || 'Wil je meer ontdekken in de buurt?';

        wrapper.appendChild(link);
        return wrapper;
    }

    buildProfileCard(profile) {
        const card = document.createElement('article');
        card.className = 'ai-profile-card';

        if (profile.logoUrl) {
            const logo = document.createElement('img');
            logo.className = 'ai-profile-card-logo';
            logo.src = profile.logoUrl;
            logo.alt = profile.name || 'Logo';
            card.appendChild(logo);
        }

        const body = document.createElement('div');
        body.className = 'ai-profile-card-body';

        const title = document.createElement('h4');
        title.className = 'ai-profile-card-title';
        title.textContent = profile.name || 'Onbekend';
        body.appendChild(title);

        if (profile.category) {
            const category = document.createElement('span');
            category.className = 'ai-profile-card-category';
            category.textContent = profile.category;
            body.appendChild(category);
        }

        if (profile.location) {
            const location = document.createElement('div');
            location.className = 'ai-profile-card-meta';
            location.textContent = `📍 ${profile.location}`;
            body.appendChild(location);
        }

        if (profile.description) {
            const description = document.createElement('div');
            description.className = 'ai-profile-card-description';
            description.textContent = profile.description;
            body.appendChild(description);
        }

        if (profile.rating) {
            const rating = document.createElement('div');
            rating.className = 'ai-profile-card-rating';
            rating.textContent = `★ ${profile.rating}`;
            body.appendChild(rating);
        }

        if (profile.profileUrl) {
            const actions = document.createElement('div');
            actions.className = 'ai-profile-card-actions';
            actions.appendChild(this.buildActionLink(profile.profileUrl, 'Bekijk profiel'));
            body.appendChild(actions);
        }

        card.appendChild(body);
        return card;
    }

    buildActionLink(href, label) {
        const link = document.createElement('a');
        link.href = href;
        link.textContent = label;
        return link;
    }

    showTypingIndicator() {
        const typingDiv = document.createElement('div');
        typingDiv.className = 'ai-message answer ai-typing-indicator';
        typingDiv.innerHTML = `
            <span class="ai-typing-label">${this.config?.operator_name || 'AI'} ${this.translations[this.currentLang].isTyping}</span>
            <span class="ai-typing-dots" aria-hidden="true"><span></span><span></span><span></span></span>
        `;
        this.elements.messages.appendChild(typingDiv);
        this.scheduleScrollToBottom();
    }

    removeTypingIndicator() {
        this.elements.messages.querySelector('.ai-typing-indicator')?.remove();
    }

    sendMessage() {
        const message = this.elements.input.value.trim();
        if (!message) {
            return;
        }

        if (!this.canSendMessages()) {
            this.setConnectionStatus('disconnected', this.translations[this.currentLang].sendFailed, true);
            return;
        }

        this.dispatchMessage(message);
        this.elements.input.value = '';
    }

    showWelcomeMessage() {
        const welcomeMsg = this.config?.welcome_message;
        if (!welcomeMsg) return;

        const message = welcomeMsg[this.currentLang] || welcomeMsg.nl || welcomeMsg.en;
        if (message) {
            this.addMessage(message, 'answer');
            this.welcomeShown = true;
            this.persistState();
        }
    }
}

function autoInit() {
    const scriptTag = document.currentScript || document.querySelector('script[data-app-id]');
    const appId = scriptTag?.getAttribute('data-app-id');
    if (!appId) return;

    const options = {
        apiBaseUrl: scriptTag.getAttribute('data-api-base-url') || undefined,
        wsUrl: scriptTag.getAttribute('data-ws-url') || undefined,
    };

    const widget = new AiChatWidget(appId, options);

    return widget;
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', autoInit);
    } else {
        autoInit();
    }
}

export default AiChatWidget;
